/**
 * BulkShareModal — share multiple interactions at once.
 *
 * Does NOT show existing grants (each interaction may differ).
 * Only shows what will be added.
 */

import React, { useState, useMemo, useCallback } from "react";
import {
  MagnifyingGlass,
  UsersThree,
  Buildings,
  X,
  CaretDown,
} from "@phosphor-icons/react";
import { useSelector } from "react-redux";

import type {
  ShareUserInput,
  ShareTeamInput,
  SharedUser,
  SharedTeam,
} from "@/services/sharing";
import { useSharingStatus, useBulkShare } from "@/hooks/useSharing";
import { displayOrganizationName } from "@/utils/organizationName";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface BulkShareModalProps {
  open: boolean;
  onClose: () => void;
  /** List of interaction public_ids to share */
  interactionIds: string[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const BulkShareModal: React.FC<BulkShareModalProps> = ({
  open,
  onClose,
  interactionIds,
}) => {
  const { organization } = useSelector((state: any) => state.userData);
  const organizationName = organization?.name
    ? `${displayOrganizationName(organization.name)} Organization`
    : "Organization";

  // Fetch available users/teams from the first interaction (all belong to same org)
  const firstId = interactionIds[0] ?? null;
  const { data: sharingStatus } = useSharingStatus(
    "interaction",
    open ? firstId : null,
    true
  );

  const bulkShareMutation = useBulkShare();

  // Accumulated selections
  const [selectedUsers, setSelectedUsers] = useState<ShareUserInput[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<ShareTeamInput[]>([]);
  const [shareWithOrg, setShareWithOrg] = useState(false);
  const [orgPermission, setOrgPermission] = useState<"view" | "edit">("edit");

  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Reset on close
  React.useEffect(() => {
    if (!open) {
      setSelectedUsers([]);
      setSelectedTeams([]);
      setShareWithOrg(false);
      setOrgPermission("edit");
      setSearchQuery("");
      setShowSearchResults(false);
    }
  }, [open]);

  // Available users/teams not yet selected
  const selectedUserIds = useMemo(
    () => new Set(selectedUsers.map((u) => u.id)),
    [selectedUsers]
  );
  const selectedTeamIds = useMemo(
    () => new Set(selectedTeams.map((t) => t.id)),
    [selectedTeams]
  );

  // Resolve names for selected items (from available lists)
  const userNameMap = useMemo(() => {
    const map = new Map<string, SharedUser>();
    for (const u of sharingStatus?.available_users ?? []) map.set(u.id, u);
    return map;
  }, [sharingStatus?.available_users]);

  const teamNameMap = useMemo(() => {
    const map = new Map<string, SharedTeam>();
    for (const t of sharingStatus?.available_teams ?? []) map.set(t.id, t);
    return map;
  }, [sharingStatus?.available_teams]);

  const searchResults = useMemo(() => {
    const lowerQuery = searchQuery.toLowerCase().trim();
    const users = (sharingStatus?.available_users ?? [])
      .filter(
        (u) =>
          !selectedUserIds.has(u.id) &&
          (!lowerQuery ||
            u.name.toLowerCase().includes(lowerQuery) ||
            u.email?.split("@")[0]?.toLowerCase().includes(lowerQuery))
      )
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

    const teams = (sharingStatus?.available_teams ?? [])
      .filter(
        (t) =>
          !selectedTeamIds.has(t.id) &&
          (!lowerQuery || t.name.toLowerCase().includes(lowerQuery))
      )
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

    return { users, teams };
  }, [searchQuery, sharingStatus, selectedUserIds, selectedTeamIds]);

  const handleAddUser = (user: SharedUser) => {
    setSelectedUsers((prev) => [...prev, { id: user.id, type: "edit" }]);
    setSearchQuery("");
    setShowSearchResults(false);
  };

  const handleAddTeam = (team: SharedTeam) => {
    setSelectedTeams((prev) => [...prev, { id: team.id, type: "edit" }]);
    setSearchQuery("");
    setShowSearchResults(false);
  };

  const handleRemoveUser = (id: string) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== id));
  };

  const handleRemoveTeam = (id: string) => {
    setSelectedTeams((prev) => prev.filter((t) => t.id !== id));
  };

  const handleApply = useCallback(() => {
    bulkShareMutation.mutate(
      {
        entity_type: "interaction",
        entity_ids: interactionIds,
        users: selectedUsers,
        teams: selectedTeams,
        organization: shareWithOrg ? { type: orgPermission } : null,
      },
      { onSuccess: () => onClose() }
    );
  }, [
    interactionIds,
    selectedUsers,
    selectedTeams,
    shareWithOrg,
    orgPermission,
    bulkShareMutation,
    onClose,
  ]);

  const hasSelections =
    selectedUsers.length > 0 || selectedTeams.length > 0 || shareWithOrg;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4">
          <h2 className="text-lg font-semibold text-gray-800">
            Share {interactionIds.length} interaction
            {interactionIds.length !== 1 ? "s" : ""}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 pb-6 space-y-4">
          {/* Search */}
          <div className="relative">
            <div className="relative">
              <MagnifyingGlass
                size={20}
                weight="regular"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Add people or teams..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSearchResults(true);
                }}
                onFocus={() => setShowSearchResults(true)}
                onBlur={() => setShowSearchResults(false)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:border-gray-800 focus:outline-none transition-colors"
              />
            </div>

            {showSearchResults &&
              (searchResults.users.length > 0 ||
                searchResults.teams.length > 0) && (
                <div
                  // Keeps the input focused (so onBlur never fires) when a
                  // result is clicked, instead of racing a blur timer
                  // against the click.
                  onMouseDown={(e) => e.preventDefault()}
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-10"
                >
                  {searchResults.users.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-start p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      onClick={() => handleAddUser(user)}
                    >
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-medium text-gray-700 mr-3 flex-shrink-0">
                        {getInitials(user.name)}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium text-gray-800">
                          {user.name}
                        </span>
                        {user.email && (
                          <span className="text-xs text-gray-500 mt-0.5">
                            {user.email}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {searchResults.teams.map((team) => (
                    <div
                      key={team.id}
                      className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      onClick={() => handleAddTeam(team)}
                    >
                      <div className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center text-white mr-3">
                        <UsersThree size={16} weight="regular" />
                      </div>
                      <span className="text-sm font-medium text-gray-800">
                        {team.name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
          </div>

          {/* Selected people/teams */}
          {(selectedUsers.length > 0 || selectedTeams.length > 0) && (
            <div>
              <h3 className="text-sm font-medium text-gray-800 mb-2">
                Will be shared with
              </h3>
              <div className="space-y-1">
                {selectedUsers.map((u) => {
                  const resolved = userNameMap.get(u.id);
                  return (
                    <div
                      key={u.id}
                      className="flex items-center justify-between py-1.5"
                    >
                      <div className="flex items-center">
                        <div className="w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center text-xs font-medium text-gray-700 mr-2">
                          {getInitials(resolved?.name ?? "?")}
                        </div>
                        <span className="text-sm text-gray-800">
                          {resolved?.name ?? u.id}
                        </span>
                      </div>
                      <button
                        onClick={() => handleRemoveUser(u.id)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  );
                })}
                {selectedTeams.map((t) => {
                  const resolved = teamNameMap.get(t.id);
                  return (
                    <div
                      key={t.id}
                      className="flex items-center justify-between py-1.5"
                    >
                      <div className="flex items-center">
                        <div className="w-7 h-7 bg-gray-800 rounded-full flex items-center justify-center text-white mr-2">
                          <UsersThree size={12} weight="regular" />
                        </div>
                        <span className="text-sm text-gray-800">
                          {resolved?.name ?? t.id}
                        </span>
                      </div>
                      <button
                        onClick={() => handleRemoveTeam(t.id)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Org-wide toggle */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center gap-2">
              <Buildings
                size={20}
                weight="regular"
                className="text-gray-600 flex-shrink-0"
              />
              <label className="flex items-center gap-2 cursor-pointer flex-1">
                <input
                  type="checkbox"
                  checked={shareWithOrg}
                  onChange={(e) => setShareWithOrg(e.target.checked)}
                  className="rounded border-gray-300 text-gray-800 focus:ring-gray-800"
                />
                <span className="text-sm font-medium text-gray-800">
                  Share with {organizationName}
                </span>
              </label>
              {shareWithOrg && (
                <div className="relative">
                  <select
                    value={orgPermission}
                    onChange={(e) =>
                      setOrgPermission(e.target.value as "view" | "edit")
                    }
                    className="px-2 py-1 pr-7 border border-gray-200 rounded text-xs font-medium appearance-none cursor-pointer bg-white focus:border-gray-800 focus:outline-none"
                  >
                    <option value="view">Viewer</option>
                    <option value="edit">Editor</option>
                  </select>
                  <CaretDown
                    size={12}
                    weight="regular"
                    className="absolute right-1.5 top-1/2 transform -translate-y-1/2 text-gray-600 pointer-events-none"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={!hasSelections || bulkShareMutation.isPending}
              className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {bulkShareMutation.isPending ? "Sharing..." : "Share"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkShareModal;
